import React, { useRef } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper function to get full URL for uploaded files
const getMediaUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('data:')) return url;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  return url;
};

// Helper to get section background style
const getSectionBgStyle = (data, defaultBg = 'transparent') => {
  if (data.usePageBgColor && data.pageBgColor) {
    return { backgroundColor: 'transparent' };
  }
  return { backgroundColor: defaultBg };
};

// Helper to get section title color
const getTitleColor = (data) => {
  return data.sectionTitleColor || '#712033';
};

// Helper to get section content color (for cards, badges, etc.)
const getContentColor = (data) => {
  return data.sectionContentColor || '#712033';
};

// For Whom Section
export const ForWhomSection = ({ data }) => {
  const titleColor = getTitleColor(data);
  const contentColor = getContentColor(data);
  return (
    <section 
      id="for-whom"
      className="for-whom-section py-20 px-6 md:px-12 scroll-mt-20" 
      style={getSectionBgStyle(data, '#ffffff')}
      data-testid="section-forWhom"
    >
      <div className="max-w-6xl mx-auto">
        <h2 
          className="section-title text-4xl md:text-5xl font-bold text-center mb-16"
          style={{ color: titleColor }}
        >
          {data.sectionTitles?.forWhom || "ДЛЯ КОГО"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.forWhom.map((item, idx) => (
            <Card key={idx} className="for-whom-card hover-card">
              <CardContent className="p-8 text-center">
                <div className="text-5xl mb-4">{item.emoji}</div>
                <h3 className="text-2xl font-bold mb-3" style={{ color: contentColor }}>{item.title}</h3>
                <p className="text-gray-600 text-lg leading-relaxed">{item.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Why Cool Section
export const WhyCoolSection = ({ data }) => {
  const titleColor = getTitleColor(data);
  const contentColor = getContentColor(data);
  const bgStyle = data.usePageBgColor && data.pageBgColor 
    ? { backgroundColor: 'transparent' } 
    : { backgroundColor: 'rgba(206, 101, 125, 0.05)' };
  
  return (
    <section 
      id="why-cool"
      className="why-cool-section py-20 px-6 md:px-12 scroll-mt-20" 
      style={bgStyle}
      data-testid="section-whyCool"
    >
      <div className="max-w-6xl mx-auto">
        <h2 
          className="section-title text-4xl md:text-5xl font-bold text-center mb-16"
          style={{ color: titleColor }}
        >
          {data.sectionTitles?.whyCool || "ЧОМУ КЛАСНО"}
        </h2>
        <div className="flex flex-wrap justify-center gap-4">
          {data.whyCool.map((item, idx) => (
            <span 
              key={idx} 
              className="why-cool-badge px-6 py-3 bg-white rounded-full font-bold text-lg shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 flex items-center gap-2"
              style={{ color: contentColor }}
            >
              <span className="text-2xl">{item.emoji}</span>
              {item.text}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

// Event Format Section
export const EventFormatSection = ({ data }) => {
  const titleColor = getTitleColor(data);
  const contentColor = getContentColor(data);
  return (
    <section 
      id="event-format"
      className="event-format-section py-20 px-6 md:px-12 scroll-mt-20" 
      style={getSectionBgStyle(data, '#ffffff')}
      data-testid="section-eventFormat"
    >
      <div className="max-w-6xl mx-auto">
        <h2 
          className="section-title text-4xl md:text-5xl font-bold text-center mb-16"
          style={{ color: titleColor }}
        >
          {data.sectionTitles?.eventFormat || "ФОРМАТ ПОДІЇ"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {data.eventFormat.map((item, idx) => (
            <div key={idx} className="event-format-item text-center p-6 rounded-xl bg-white shadow-md hover:shadow-lg transition-all duration-300">
              <div className="text-4xl mb-4">{item.emoji}</div>
              <p className="text-lg font-bold" style={{ color: contentColor }}>{item.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Personal Brand Section
export const PersonalBrandSection = ({ data }) => (
  <section id="personal-brand" className="personal-brand-section py-20 px-6 md:px-12 bg-[#712033] text-white scroll-mt-20" data-testid="section-personalBrand">
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-center gap-4 mb-16">
        <span className="text-6xl">{data.personalBrand?.emoji || '💎'}</span>
        <h2 className="section-title text-4xl md:text-5xl font-bold text-center">
          {data.personalBrand?.title || "ПРО ОСОБИСТИЙ БРЕНД"}
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {data.personalBrand?.items?.map((item, idx) => (
          <div key={idx} className="personal-brand-item flex items-center gap-4 p-6 rounded-xl bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all duration-300">
            <span className="text-3xl">{item.emoji}</span>
            <p className="text-lg font-medium">{item.text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Gallery Section
export const GallerySection = ({ data, openGallery }) => {
  const titleColor = getTitleColor(data);
  const scrollContainerRef = useRef(null);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -340, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 340, behavior: 'smooth' });
    }
  };

  return (
    <section 
      id="gallery"
      className="gallery-section py-20 px-6 md:px-12 scroll-mt-20" 
      style={getSectionBgStyle(data, '#ffffff')}
      data-testid="section-gallery"
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-12 flex-wrap gap-4">
          <div className="w-full text-center relative">
            <h2 
              className="section-title text-4xl md:text-5xl font-bold"
              style={{ color: titleColor }}
            >
              {data.sectionTitles?.gallery || "ЯК ЦЕ БУЛО В ОДЕСІ"}
            </h2>
          </div>
        </div>

        {/* Gallery Carousel with Controls */}
        <div className="relative group">
          {/* Scroll Buttons for Desktop */}
          <div className="hidden md:flex justify-end gap-3 mb-4">
            <button 
              onClick={scrollLeft} 
              aria-label="Попередні фото" 
              className="gallery-nav-btn"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button 
              onClick={scrollRight} 
              aria-label="Наступні фото" 
              className="gallery-nav-btn"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>

          <div 
            ref={scrollContainerRef}
            className="gallery-horizontal" 
            data-testid="gallery-horizontal"
          >
            {data.gallery?.map((img, idx) => (
              <div
                key={img.id || idx}
                role="button"
                tabIndex={0}
                aria-label={`Переглянути фото ${idx + 1}: ${img.alt || 'Gastro Bloggers Day'}`}
                className="gallery-horizontal-item focus:outline-none focus:ring-2 focus:ring-[#712033]"
                onClick={() => openGallery(idx)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openGallery(idx);
                  }
                }}
                data-testid={`gallery-item-${idx}`}
              >
                <img 
                  src={getMediaUrl(img.url)} 
                  alt={img.alt || `Event ${idx + 1}`} 
                  loading="lazy"
                  decoding="async"
                  width="320"
                  height="220"
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
          <p className="text-center text-gray-500 text-sm mt-4 md:hidden">
            ← Проведіть пальцем для перегляду фото →
          </p>
        </div>
      </div>
    </section>
  );
};

// Video Section
export const VideoSection = ({ data, videoRef }) => {
  const titleColor = getTitleColor(data);
  const bgStyle = data.usePageBgColor && data.pageBgColor 
    ? { backgroundColor: 'transparent' } 
    : { backgroundColor: 'rgba(206, 101, 125, 0.05)' };
    
  if (!data.video?.url) return null;
  
  const videoUrl = getMediaUrl(data.video.url);
  const posterUrl = data.video.poster ? getMediaUrl(data.video.poster) : '';
  
  return (
    <section 
      id="video"
      className="video-section py-20 px-6 md:px-12 scroll-mt-20" 
      style={bgStyle}
      data-testid="section-video"
    >
      <div className="max-w-4xl mx-auto">
        <h2 
          className="section-title text-4xl md:text-5xl font-bold text-center mb-16"
          style={{ color: titleColor }}
        >
          {data.video.title}
        </h2>
        <div className="video-container rounded-2xl overflow-hidden shadow-2xl bg-black max-w-[360px] md:max-w-[420px] mx-auto flex items-center justify-center border border-[#d4af37]/20">
          <video 
            ref={videoRef}
            className="w-full h-auto max-h-[80vh] object-contain rounded-2xl mx-auto block"
            controls
            playsInline
            preload="metadata"
            data-testid="video-player"
            poster={posterUrl}
          >
            <source src={videoUrl} type="video/mp4" />
            Ваш браузер не підтримує відео.
          </video>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
export const TestimonialsSection = ({ data }) => {
  const titleColor = getTitleColor(data);
  return (
    <section 
      id="testimonials"
      className="testimonials-section py-20 px-6 md:px-12 scroll-mt-20" 
      style={getSectionBgStyle(data, '#ffffff')}
      data-testid="section-testimonials"
    >
      <div className="max-w-6xl mx-auto">
        <h2 
          className="section-title text-4xl md:text-5xl font-bold text-center mb-16"
          style={{ color: titleColor }}
        >
          {data.sectionTitles?.testimonials || "ВІДГУКИ"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {data.testimonials?.map((testimonial) => (
            <div key={testimonial.id} className="testimonial-messenger bg-white rounded-2xl shadow-lg overflow-hidden hover-card">
              <div className="bg-gradient-to-r from-[#712033] to-[#ce657d] p-4 flex items-center gap-3">
                <img 
                  src={testimonial.avatar} 
                  alt={testimonial.name}
                  loading="lazy"
                  decoding="async"
                  width="48"
                  height="48"
                  className="w-12 h-12 rounded-full border-2 border-white object-cover"
                />
                <div>
                  <p className="font-bold text-white text-base">{testimonial.name}</p>
                  <p className="text-xs text-white/85">{testimonial.role}</p>
                </div>
              </div>
              <div className="p-6">
                <div className="bg-gray-100 rounded-2xl rounded-tl-none p-4 mb-2">
                  <p className="text-gray-800 leading-relaxed text-sm md:text-base">{testimonial.text}</p>
                </div>
                <p className="text-xs text-gray-500 text-right">{testimonial.timestamp}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Tickets Section
export const TicketsSection = ({ data }) => {
  const titleColor = getTitleColor(data);
  const contentColor = getContentColor(data);

  return (
    <section 
      id="tickets"
      className="tickets-section py-20 px-6 md:px-12 scroll-mt-20" 
      style={getSectionBgStyle(data, '#ffffff')}
      data-testid="section-tickets"
    >
      <div className="max-w-6xl mx-auto">
        <h2 
          className="section-title text-4xl md:text-5xl font-bold text-center mb-16"
          style={{ color: titleColor }}
        >
          {data.sectionTitles?.tickets || "КВИТКИ"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8 items-stretch">
          {data.tickets?.map((ticket) => {
            const hasCounter = ticket.counter && typeof ticket.counter.total === 'number' && ticket.counter.total > 0;
            const remaining = hasCounter ? ticket.counter.current : 0;
            const total = hasCounter ? ticket.counter.total : 0;
            const soldPercent = hasCounter ? Math.min(100, Math.max(0, ((total - remaining) / total) * 100)) : 0;

            return (
              <Card 
                key={ticket.id} 
                className={`ticket-card hover-card flex flex-col justify-between ${ticket.id === 1 ? 'border-4 border-[#ce657d] shadow-xl' : 'border border-gray-200'}`}
              >
                <CardContent className="p-8 text-center flex flex-col h-full justify-between">
                  <div>
                    {ticket.label ? (
                      <span className="inline-block px-4 py-1.5 bg-[#ce657d] text-white rounded-full text-xs md:text-sm font-bold mb-4 shadow-sm">
                        {ticket.label}
                      </span>
                    ) : (
                      <div className="h-8 mb-4"></div>
                    )}
                    <h3 className="text-2xl font-bold mb-3" style={{ color: contentColor }}>{ticket.name}</h3>
                    
                    {ticket.showOriginalPrice && ticket.originalPrice && (
                      <p className="text-gray-400 line-through text-lg mb-1 font-semibold">{ticket.originalPrice} грн</p>
                    )}
                    
                    {/* Ticket price with gold gradient effect */}
                    <p className="text-5xl font-bold mb-4 gold-gradient-text">{ticket.price} грн</p>
                    
                    {ticket.description && (
                      <p className="text-gray-600 mb-4 text-sm leading-relaxed">{ticket.description}</p>
                    )}

                    {/* Visual Progress Bar for Available / Sold Seats */}
                    {hasCounter && (
                      <div className="mb-6 bg-gray-50 p-3.5 rounded-xl border border-gray-100">
                        <div className="flex justify-between items-center text-xs md:text-sm font-semibold mb-2">
                          <span className="text-gray-600">Залишилось місць:</span>
                          <span className="text-[#712033] font-bold">{remaining} з {total}</span>
                        </div>
                        <div className="ticket-progress-track">
                          <div 
                            className="ticket-progress-fill" 
                            style={{ width: `${soldPercent}%` }}
                            title={`Продано ${Math.round(soldPercent)}%`}
                          ></div>
                        </div>
                      </div>
                    )}

                    {ticket.note && (
                      <p className="text-gray-500 mb-6 text-xs md:text-sm italic">{ticket.note}</p>
                    )}
                  </div>

                  <div className="mt-4">
                    {ticket.available === false ? (
                      <Button disabled className="w-full bg-gray-200 text-gray-500 font-bold py-6 cursor-not-allowed">
                        {ticket.note || 'Розпродано'}
                      </Button>
                    ) : ticket.buttonText ? (
                      <Button asChild className="gold-gradient-btn w-full py-6 text-base shadow-md">
                        <a href={ticket.buttonLink} target="_blank" rel="noopener noreferrer">
                          {ticket.buttonText}
                        </a>
                      </Button>
                    ) : null}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
        <p className="text-center text-gray-500 text-sm">Ціна зростає по мірі заповнення місць.</p>
      </div>
    </section>
  );
};

// Partners Section
export const PartnersSection = ({ data }) => (
  <section id="partners" className="partners-section py-20 px-6 md:px-12 bg-[#712033] text-white scroll-mt-20" data-testid="section-partners">
    <div className="max-w-6xl mx-auto">
      <h2 className="section-title text-4xl md:text-5xl font-bold text-center mb-16">
        {data.sectionTitles?.partners || data.partners?.title || "ПАРТНЕРИ"}
      </h2>
      <div className="flex flex-wrap justify-center gap-4 mb-12">
        {data.partners?.features?.map((feature, idx) => (
          <span key={idx} className="partner-feature px-6 py-3 bg-white/10 backdrop-blur-sm rounded-full font-bold text-lg hover:bg-white/20 transition-all duration-300">
            {feature}
          </span>
        ))}
      </div>
      <div className="flex flex-wrap justify-center gap-4">
        {data.partners?.buttons?.map((btn, idx) => (
          <Button key={idx} asChild className="gold-gradient-btn w-full md:w-auto">
            <a href={btn.link} target="_blank" rel="noopener noreferrer">
              {btn.text}
            </a>
          </Button>
        ))}
      </div>
    </div>
  </section>
);

// Organizers Section
export const OrganizersSection = ({ data, expandedOrganizers, toggleOrganizer }) => {
  const titleColor = getTitleColor(data);
  const contentColor = getContentColor(data);
  
  if (!data.organizers || data.organizers.length === 0) return null;
  
  return (
    <section 
      id="organizers"
      className="organizers-section py-20 px-6 md:px-12 scroll-mt-20" 
      style={getSectionBgStyle(data, '#ffffff')}
      data-testid="section-organizers"
    >
      <div className="max-w-6xl mx-auto">
        <h2 
          className="section-title text-4xl md:text-5xl font-bold text-center mb-16"
          style={{ color: titleColor }}
        >
          {data.sectionTitles?.organizers || "ОРГАНІЗАТОРКИ"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {data.organizers.map((organizer) => (
            <Card key={organizer.id} className="organizer-card hover-card">
              <CardContent className="p-8 text-center">
                <img 
                  src={getMediaUrl(organizer.photo)} 
                  alt={organizer.name}
                  loading="lazy"
                  decoding="async"
                  width="128"
                  height="128"
                  className="w-32 h-32 rounded-full mx-auto mb-6 object-cover border-4 border-[#712033]"
                />
                <h3 className="text-2xl font-bold mb-3" style={{ color: contentColor }}>{organizer.name}</h3>
                <div className="relative">
                  <p 
                    className={`text-gray-600 mb-4 whitespace-pre-line transition-all duration-300 leading-relaxed ${
                      expandedOrganizers[organizer.id] ? '' : 'line-clamp-3'
                    }`}
                    style={{ textAlign: organizer.descriptionAlign || 'center' }}
                  >
                    {organizer.description}
                  </p>
                  {organizer.description && organizer.description.length > 80 && (
                    <button
                      onClick={() => toggleOrganizer(organizer.id)}
                      className="font-semibold flex items-center gap-1 mx-auto hover:opacity-80 transition-opacity"
                      style={{ color: contentColor }}
                    >
                      {expandedOrganizers[organizer.id] ? (
                        <>Згорнути <ChevronUp className="w-4 h-4" /></>
                      ) : (
                        <>Читати більше <ChevronDown className="w-4 h-4" /></>
                      )}
                    </button>
                  )}
                </div>
                {organizer.socialLink && (
                  <a
                    href={organizer.socialLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 mt-4 bg-[#712033]/10 hover:bg-[#712033]/20 rounded-full font-medium transition-colors"
                    style={{ color: contentColor }}
                  >
                    {organizer.socialText || 'Соц. мережа'}
                  </a>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};
